import { PaginatedDataDTO } from "@bottega52/commons-pagination";
import { Button, Card } from "@material-ui/core";
import { WithStyles, createStyles, withStyles } from '@material-ui/core/styles';
import NotificationsActiveOutlinedIcon from '@material-ui/icons/NotificationsActiveOutlined';
import WarningIcon from '@material-ui/icons/Warning';
import * as React from "react";
import { ConnectedProps, connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as CustomerContractNotificationRuleDecoder from '../../codec/customerContractNotificationRuleInDTODecoder';
import * as CreditsActions from '../../redux/credits/credits.actions';
import * as ModalsActions from '../../redux/modals/modals.actions';
import * as SettingsActions from '../../redux/settings/settings.actions';
import { IState } from '../../redux/store';
import { ICustomerContractInDTO } from "../../repository/jago/model/input/ICustomerContractInDTO";
import { ICustomerContractNotificationRuleFormDTO } from "../../repository/jago/model/input/ICustomerContractNotificationRuleFormDTO";
import { ICustomerContractNotificationRuleInDTO } from "../../repository/jago/model/input/ICustomerContractNotificationRuleInDTO";
import { ICustomerContractServiceInDTO } from "../../repository/jago/model/input/ICustomerContractServiceInDTO";
import { ICustomerInDTO } from "../../repository/jago/model/input/ICustomerInDTO";
import AbilityProvider from "../../services/PermissionService/AbilityProvider";
import { PERMISSIONS } from "../../services/PermissionService/PermissionConstants";
import CustomerContractNotificationRuleForm from "../Forms/CustomerContractNotificationRuleForm";
import { ModalTypes } from "../Modals/ModalTypes";
import ReduxLanguage from "../ReduxLanguage";
import CustomerContractNotificationsTable from "./CustomerContractNotificationsTable";

const styles = createStyles({
  container: {
    display: 'flex',
    marginTop: 20,
    flexDirection: 'column',
  },
  cardContainer: {
    padding: 10,
    borderLeft: '3px solid #5AC0B1',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerInner: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTextContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  icon: {
    marginRight: 5,
    fontSize: 25,
  },
  title: {
    fontWeight: 'bold'
  },
  tableContainer: {
    display: 'flex',
    backgroundColor: 'white',
    height: 'calc(100vh - 380px)',
    width: '100%',
    marginTop: 10
  }
});

export interface ICustomerContractNotificationsSectionState {
  pageSize: number;
}

type IReduxProps = ConnectedProps<typeof connector> & RouteComponentProps<any>;

export interface ICustomerContractNotificationsSectionProps extends WithStyles<typeof styles> {
  language: string;
  customer: ICustomerInDTO;
}

export type ComponentProps = ICustomerContractNotificationsSectionProps & IReduxProps;

class CustomerContractNotificationsSection extends React.Component<ComponentProps, ICustomerContractNotificationsSectionState> {

  constructor(props: ComponentProps) {
    super(props);
    this.state = {
      pageSize: 1000
    };
  }

  async componentDidMount() {
    await this.loadNotificationRules();
  }

  async componentDidUpdate(prevProps: ComponentProps) {
    const { customer, selectedCustomerContract } = this.props;
    if (
      (customer && selectedCustomerContract) &&
      (
        prevProps.customer?.id !== customer.id ||
        (prevProps.selectedCustomerContract as ICustomerContractInDTO)?.id !== (selectedCustomerContract as ICustomerContractInDTO)?.id
      )
    ) {
      await this.loadNotificationRules();
    }
  }

  async loadNotificationRules() {
    const { dispatch, customer, selectedCustomerContract } = this.props;
    if (customer && selectedCustomerContract) {
      const promises = [
        dispatch<any>(CreditsActions.fetchCustomerContractNotificationRules(customer.id, (selectedCustomerContract as ICustomerContractInDTO).id, { 
          page: 0, 
          pageSize: this.state.pageSize 
        })),
        dispatch<any>(CreditsActions.fetchCustomerContractServices({ 
          customerId: customer.id, 
          contractId: (selectedCustomerContract as ICustomerContractInDTO).id,
          page: 0, 
          pageSize: 100 
        }))
      ];
      await Promise.all(promises);
    }
  }

  async onOpenEditCustomerContractNotificationRuleForm(notificationRule: ICustomerContractNotificationRuleInDTO) {
    const { dispatch } = this.props;
    const availableServices = (this.props.customerContractServices as PaginatedDataDTO<ICustomerContractServiceInDTO>)?.content || [];
    const notificationRuleFormData = CustomerContractNotificationRuleDecoder.decodeCustomerContractNotificationRuleContentToForm(notificationRule, availableServices);
    dispatch<any>(ModalsActions.showModal(`OPERATIONAL_CUSTOMER_CONTRACT_NOTIFICATION_RULE_MODAL`, {
      modalType: ModalTypes.OPERATIONAL_VIEW_MODAL,
      modalProps: {
        content: (
          <CustomerContractNotificationRuleForm
            notificationRule={notificationRuleFormData}
            contractServices={availableServices}
            onCreateEditNotificationRule={(newNotificationRuleData: ICustomerContractNotificationRuleFormDTO) => this.onEditCustomerContractNotificationRuleRequest(notificationRule.id, newNotificationRuleData)}
          />
        ),
        titleMessageKey: 'credits.editNotificationRule',
      }
    }));
  }

  async onOpenNewCustomerContractNotificationRuleForm() {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`OPERATIONAL_CUSTOMER_CONTRACT_NOTIFICATION_RULE_MODAL`, {
      modalType: ModalTypes.OPERATIONAL_VIEW_MODAL_BACKDROP,
      modalProps: {
        content: (
          <CustomerContractNotificationRuleForm
            contractServices={(this.props.customerContractServices as PaginatedDataDTO<ICustomerContractServiceInDTO>)?.content || []}
            onCreateEditNotificationRule={(newNotificationRuleData: ICustomerContractNotificationRuleFormDTO) => this.onCreateNewCustomerContractNotificationRuleRequest(newNotificationRuleData)}
          />
        ),
        titleMessageKey: 'credits.newNotificationRule',
      }
    }));
  }

  public onEditCustomerContractNotificationRuleRequest(notificationRuleId: number, newNotificationRuleData: ICustomerContractNotificationRuleFormDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`EDIT_CUSTOMER_CONTRACT_NOTIFICATION_RULE_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'credits.editNotificationRule',
        successMessageKey: 'credits.notificationRuleEditConfirm',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onEditCustomerContractNotificationRuleConfirm(notificationRuleId, newNotificationRuleData),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`EDIT_CUSTOMER_CONTRACT_NOTIFICATION_RULE_CONFIRM_MODAL`)),
      }
    }));
  }

  public async onEditCustomerContractNotificationRuleConfirm(notificationRuleId: number, newNotificationRuleData: ICustomerContractNotificationRuleFormDTO) {
    const { dispatch, customer, selectedCustomerContract } = this.props;
    try {
      dispatch<any>(ModalsActions.hideModal(`EDIT_CUSTOMER_CONTRACT_NOTIFICATION_RULE_CONFIRM_MODAL`));
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      const newNotificationRuleDataEncoded = CustomerContractNotificationRuleDecoder.encodeCustomerContractNotificationRuleFromForm(newNotificationRuleData);
      const notificationRuleUpdated = await dispatch<any>(CreditsActions.editCustomerContractNotificationRule(customer.id, (selectedCustomerContract as ICustomerContractInDTO).id, notificationRuleId, newNotificationRuleDataEncoded));
      dispatch<any>(ModalsActions.hideModal(`OPERATIONAL_CUSTOMER_CONTRACT_NOTIFICATION_RULE_MODAL`));
      dispatch<any>(CreditsActions.setSelectedCustomerContractNotificationRule(notificationRuleUpdated));
      await dispatch<any>(CreditsActions.fetchCustomerContractNotificationRules(customer.id, (selectedCustomerContract as ICustomerContractInDTO).id, { 
        page: 0, 
        pageSize: this.state.pageSize 
      }));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
    } catch (error) {
      console.log(error);
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_CUSTOMER_CONTRACT_NOTIFICATION_RULE_EDIT`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'credits.editCustomerContractNotificationRuleError',
        }
      }));
    }
  }

  public onCreateNewCustomerContractNotificationRuleRequest(newNotificationRuleData: ICustomerContractNotificationRuleFormDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`CREATE_CUSTOMER_CONTRACT_NOTIFICATION_RULE_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'credits.newNotificationRule',
        successMessageKey: 'credits.notificationRuleCreationConfirm',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onCreateNewCustomerContractNotificationRuleConfirm(newNotificationRuleData),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`CREATE_CUSTOMER_CONTRACT_NOTIFICATION_RULE_CONFIRM_MODAL`)),
      }
    }));
  }

  public async onCreateNewCustomerContractNotificationRuleConfirm(newNotificationRuleData: ICustomerContractNotificationRuleFormDTO) {
    const { dispatch, customer, selectedCustomerContract } = this.props;
    try {
      dispatch<any>(ModalsActions.hideModal(`CREATE_CUSTOMER_CONTRACT_NOTIFICATION_RULE_CONFIRM_MODAL`));
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      const newNotificationRuleDataEncoded = CustomerContractNotificationRuleDecoder.encodeCustomerContractNotificationRuleFromForm(newNotificationRuleData);
      await dispatch<any>(CreditsActions.createNewCustomerContractNotificationRule(customer.id, (selectedCustomerContract as ICustomerContractInDTO).id, newNotificationRuleDataEncoded));
      dispatch<any>(ModalsActions.hideModal(`OPERATIONAL_CUSTOMER_CONTRACT_NOTIFICATION_RULE_MODAL`));
      await dispatch<any>(CreditsActions.fetchCustomerContractNotificationRules(customer.id, (selectedCustomerContract as ICustomerContractInDTO).id, { 
        page: 0, 
        pageSize: this.state.pageSize 
      }));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
    } catch (error) {
      console.log(error);
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_CUSTOMER_CONTRACT_NOTIFICATION_RULE_CREATION`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'credits.createCustomerContractNotificationRuleError',
        }
      }));
    }
  }

  async onFetchCustomerContractNotificationRulesOnPage(page: number) {
    const { dispatch, customer, selectedCustomerContract } = this.props;
    await dispatch<any>(CreditsActions.fetchCustomerContractNotificationRules(customer.id, (selectedCustomerContract as ICustomerContractInDTO).id, { 
      page, 
      pageSize: this.state.pageSize 
    }));
  }

  async onPageSizeChange(pageSize: number) {
    this.setState({ pageSize });
    const { dispatch, customer, selectedCustomerContract } = this.props;
    await dispatch<any>(CreditsActions.fetchCustomerContractNotificationRules(customer.id, (selectedCustomerContract as ICustomerContractInDTO).id, { 
      page: 0, 
      pageSize 
    }));
  }

  onDeleteCustomerContractNotificationRuleRequest(notificationRule: ICustomerContractNotificationRuleInDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`DELETE_CUSTOMER_CONTRACT_NOTIFICATION_RULE_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'red', fontSize: 50 }} />,
        titleMessageKey: 'credits.deleteNotificationRule',
        successMessageKey: 'credits.deleteNotificationRuleConfirm',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onDeleteCustomerContractNotificationRuleConfirm(notificationRule),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`DELETE_CUSTOMER_CONTRACT_NOTIFICATION_RULE_CONFIRM_MODAL`)),
      }
    }));
  }

  async onDeleteCustomerContractNotificationRuleConfirm(notificationRule: ICustomerContractNotificationRuleInDTO) {
    const { dispatch, customer, selectedCustomerContract } = this.props;
    try {
      dispatch<any>(ModalsActions.hideModal(`DELETE_CUSTOMER_CONTRACT_NOTIFICATION_RULE_CONFIRM_MODAL`));
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      await dispatch<any>(CreditsActions.deleteCustomerContractNotificationRule(customer.id, (selectedCustomerContract as ICustomerContractInDTO).id, notificationRule.id));
      await dispatch<any>(CreditsActions.fetchCustomerContractNotificationRules(customer.id, (selectedCustomerContract as ICustomerContractInDTO).id, { 
        page: 0, 
        pageSize: this.state.pageSize 
      }));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
    } catch (error) {
      console.log(error);
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_CUSTOMER_CONTRACT_NOTIFICATION_RULE_DELETION`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'credits.deleteCustomerContractNotificationRuleError',
        }
      }));
    }
  }

  public render() {
    const { customerContractNotificationRules, classes } = this.props;
    const { pageSize } = this.state;
    
    const canCreateCustomerContractNotificationRule = AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CONTRACT_SERVICES_CREATE);

    return (
      <div className={classes.container}>
        <Card className={classes.cardContainer}>
          <div className={classes.header}>
            <div className={classes.headerInner}>
              <NotificationsActiveOutlinedIcon className={classes.icon} />
              <h3 className={classes.title}><ReduxLanguage languageKey="credits.contractNotifications" /></h3>
              <h5 className={classes.title} style={{ marginLeft: 0 }}>{` (${((customerContractNotificationRules as PaginatedDataDTO<ICustomerContractNotificationRuleInDTO>).content || []).length})`}</h5>
            </div>
            {canCreateCustomerContractNotificationRule && 
              <Button
                variant="contained"
                style={{ fontWeight: 'bold', backgroundColor: '#5AC0B1', fontSize: 11, color: 'white' }}
                onClick={() => {this.onOpenNewCustomerContractNotificationRuleForm()}}
              >
                <ReduxLanguage languageKey="credits.newNotificationRule" />
              </Button>
            }
          </div>
          <div className={classes.tableContainer}>
            <CustomerContractNotificationsTable
              rowsPerPage={pageSize}
              customerContractNotificationRules={customerContractNotificationRules as PaginatedDataDTO<ICustomerContractNotificationRuleInDTO>}
              onRowClick={(notificationRule) => {this.onOpenEditCustomerContractNotificationRuleForm(notificationRule)}}
              onPageSizeChange={pageSize => this.onPageSizeChange(pageSize)}
              onFetchElementsOnPage={page => this.onFetchCustomerContractNotificationRulesOnPage(page)}
              onDeleteNotificationRuleRequest={(notificationRule) => this.onDeleteCustomerContractNotificationRuleRequest(notificationRule)}
              customerContractServices={(this.props.customerContractServices as PaginatedDataDTO<ICustomerContractServiceInDTO>)?.content || []}
              hideFooter={true}
            />
          </div>
        </Card>
      </div>
    );
  }
}

function mapStateToProps(state: IState) {
  return {
    language: state.settings.language,
    userData: state.user.userData,
    selectedCustomerContract: state.credits.customerContracts.selectedCustomerContract,
    selectedCustomerContractNotificationRule: state.credits.customerContractNotificationRules.selectedCustomerContractNotificationRule,
    customerContractNotificationRules: state.credits.customerContractNotificationRules.data,
    customerContractServices: state.credits.customerContractServices.data,
  };
}

const connector = connect(mapStateToProps);

export default connector(withRouter(withStyles(styles)(CustomerContractNotificationsSection))); 